<template>
  <div class="card fb-properties">
    <div
      v-if="!localField"
      class="card-body fb-properties__empty"
    >
      <feather-icon
        icon="SlidersIcon"
        size="30"
        class="mb-1"
      />
      <h6 class="mb-50">
        Nenhum campo selecionado
      </h6>
      <p class="mb-0">
        Clique em um campo no canvas para editar rótulo, tipo, opções e lógica.
      </p>
    </div>

    <template v-else>
      <b-tabs
        v-model="activeTab"
        class="fb-properties__tabs"
      >
        <b-tab title="Propriedades">
          <div class="card-body">
            <div class="form-group">
              <label>Rótulo:</label>
              <b-form-input
                v-model="localField.label"
                maxlength="255"
                @blur="emitUpdate"
              />
            </div>
            <div class="form-group">
              <label>Texto de ajuda:</label>
              <b-form-textarea
                v-model="localField.help_text"
                rows="2"
                @blur="emitUpdate"
              />
            </div>
            <div class="form-group">
              <label>Tipo de campo:</label>
              <select
                v-model="localField.field_type"
                class="form-control"
                @change="emitUpdate"
              >
                <option
                  v-for="type in fieldTypes"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div
              v-if="localField.field_type !== 'section_break'"
              class="form-group"
            >
              <b-form-checkbox
                v-model="localField.required"
                switch
                @change="emitUpdate"
              >
                Obrigatória
              </b-form-checkbox>
            </div>

            <!-- single_choice / multiple_choice / image_annotation: editor de opções -->
            <template v-if="['single_choice', 'multiple_choice', 'image_annotation'].includes(localField.field_type)">
              <hr>
              <label>Opções:</label>
              <div
                v-for="(opt, idx) in localField.options"
                :key="idx"
                class="card border mb-2 p-2 fb-option-card"
              >
                <div class="form-row mb-1">
                  <div class="form-group col-6 mb-0">
                    <label class="fb-opt-label">Rótulo:</label>
                    <b-form-input
                      v-model="opt.label"
                      size="sm"
                      @blur="emitUpdate"
                    />
                  </div>
                  <div class="form-group col-6 mb-0">
                    <label class="fb-opt-label">Valor:</label>
                    <b-form-input
                      v-model="opt.value"
                      size="sm"
                      @blur="emitUpdate"
                    />
                  </div>
                </div>
                <div
                  v-if="localField.field_type === 'single_choice'"
                  class="form-group mb-1"
                >
                  <label class="fb-opt-label">Próximo campo:</label>
                  <v-select
                    v-model="opt.next_field_id"
                    :options="otherFields"
                    label="label"
                    :reduce="f => f.id"
                    placeholder="Segue a ordem normal"
                    class="vselect"
                    @input="emitUpdate"
                  />
                </div>
                <div class="form-group mb-1">
                  <label class="fb-opt-label">Classificação:</label>
                  <v-select
                    v-model="opt.classification_id"
                    :options="classifications"
                    :reduce="c => c.id"
                    label="color_label"
                    placeholder="Nenhuma"
                    :clearable="true"
                    class="vselect"
                    @input="emitUpdate"
                  >
                    <template #option="o">
                      <span
                        class="d-inline-block rounded-circle mr-1"
                        style="width:12px;height:12px;vertical-align:middle"
                        :style="{ background: o.hex_color }"
                      />
                      {{ o.color_label }}
                    </template>
                  </v-select>
                </div>
                <div class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    @click="removeOption(idx)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="12"
                    />
                    Remover
                  </b-button>
                </div>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="addOption"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="13"
                  class="mr-25"
                />
                Adicionar opção
              </b-button>
            </template>

            <!-- image_annotation: imagem base -->
            <template v-if="localField.field_type === 'image_annotation'">
              <hr>
              <div class="form-group">
                <label>Imagem base:</label>
                <div
                  v-if="localField.base_image_url"
                  class="mb-1"
                >
                  <img
                    :src="localField.base_image_url"
                    style="max-width: 100%; max-height: 140px; border: 1px solid #ddd; border-radius: 4px"
                  >
                </div>
                <input
                  type="file"
                  accept="image/*"
                  class="form-control-file"
                  @change="onImageChange"
                >
              </div>
            </template>

            <hr>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="confirmDelete"
            >
              <feather-icon
                icon="TrashIcon"
                size="13"
                class="mr-50"
              />
              Excluir campo
            </b-button>
          </div>
        </b-tab>

        <b-tab title="Lógica">
          <div class="card-body">
            <!-- boolean -->
            <template v-if="localField.field_type === 'boolean'">
              <div class="form-group">
                <label>Se responder Sim:</label>
                <v-select
                  v-model="localField.next_field_id_if_yes"
                  :options="yesNoOptions"
                  label="label"
                  :reduce="f => f.id"
                  class="vselect"
                  @input="emitUpdate"
                />
              </div>
              <div class="form-group">
                <label>Se responder Não:</label>
                <v-select
                  v-model="localField.next_field_id_if_no"
                  :options="yesNoOptions"
                  label="label"
                  :reduce="f => f.id"
                  class="vselect"
                  @input="emitUpdate"
                />
              </div>
              <small class="text-muted">
                Sem um próximo campo definido, o formulário simplesmente segue a ordem normal — não é preciso configurar nada aqui a menos que este campo precise desviar o fluxo.
              </small>
            </template>

            <!-- single_choice: resumo somente leitura -->
            <template v-else-if="localField.field_type === 'single_choice'">
              <p class="text-muted small">
                Edite o próximo campo de cada opção na aba <strong>Propriedades</strong>.
              </p>
              <ul class="fb-logic-summary">
                <li
                  v-for="opt in localField.options"
                  :key="opt.id || opt.label"
                >
                  <strong>{{ opt.label || '(sem rótulo)' }}</strong>
                  →
                  {{ opt.next_field_id ? fieldLabelById(opt.next_field_id) : 'segue a ordem normal' }}
                </li>
              </ul>
            </template>

            <!-- demais tipos: sem lógica condicional -->
            <template v-else>
              <p class="text-muted small mb-0">
                Este tipo de campo não tem lógica condicional — o formulário segue para o próximo campo normalmente.
              </p>
            </template>
          </div>
        </b-tab>
      </b-tabs>
    </template>
  </div>
</template>

<script>
import {
  BTabs, BTab, BFormInput, BFormTextarea, BFormCheckbox, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import swal from 'sweetalert'
import { FIELD_TYPE_CATALOG } from './fieldTypes'

export default {
  name: 'FieldProperties',
  components: {
    BTabs, BTab, BFormInput, BFormTextarea, BFormCheckbox, BButton, vSelect,
  },
  props: {
    field: { type: Object, default: null },
    allFields: { type: Array, default: () => [] },
    classifications: { type: Array, default: () => [] },
  },
  data() {
    return {
      localField: this.cloneField(this.field),
      removedOptionIds: [],
      imageFile: null,
      activeTab: 0,
    }
  },
  computed: {
    fieldTypes() {
      return FIELD_TYPE_CATALOG
    },
    otherFields() {
      if (!this.localField) return []
      return this.allFields.filter(f => f.id !== this.localField.id)
    },
    yesNoOptions() {
      return [
        { id: null, label: 'Nenhum (segue a ordem normal)' },
        ...this.otherFields,
      ]
    },
  },
  watch: {
    // Troca de campo selecionado (ou atualização vinda do pai após salvar):
    // reclona para uma cópia de edição local, descartando estado de edição
    // pendente do campo anterior.
    field(newField) {
      this.localField = this.cloneField(newField)
      this.removedOptionIds = []
      this.imageFile = null
      this.activeTab = 0
    },
  },
  methods: {
    cloneField(f) {
      if (!f) return null
      return {
        ...f,
        options: (f.options || []).map(o => ({ ...o })),
      }
    },
    fieldLabelById(id) {
      const found = this.allFields.find(f => f.id === id)
      return found ? found.label : '?'
    },
    addOption() {
      this.localField.options.push({
        label: '', value: '', next_field_id: null, classification_id: null,
      })
    },
    removeOption(idx) {
      const opt = this.localField.options[idx]
      if (opt && opt.id) this.removedOptionIds.push(opt.id)
      this.localField.options.splice(idx, 1)
      this.emitUpdate()
    },
    onImageChange(e) {
      const file = (e.target.files && e.target.files[0]) || null
      this.imageFile = file
      if (file) this.emitUpdate()
    },
    emitUpdate() {
      if (!this.localField) return
      const f = this.localField
      this.$emit('update', {
        id: f.id,
        data: {
          form_definition_id: f.form_definition_id,
          key: f.key,
          label: f.label,
          field_type: f.field_type,
          required: !!f.required,
          help_text: f.help_text || null,
          order: f.order,
          is_root: !!f.is_root,
          next_field_id_if_yes: f.next_field_id_if_yes || null,
          next_field_id_if_no: f.next_field_id_if_no || null,
          classification_id: f.classification_id || null,
          status: f.status,
        },
        options: (f.options || []).map((o, idx) => ({ ...o, order: idx })),
        removedOptionIds: this.removedOptionIds,
        imageFile: this.imageFile,
      })
      this.removedOptionIds = []
      this.imageFile = null
    },
    confirmDelete() {
      swal({
        title: `Excluir "${this.localField.label}"?`,
        text: 'Esta ação não pode ser desfeita.',
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      }).then(confirmed => {
        if (confirmed) this.$emit('delete', this.localField.id)
      })
    },
  },
}
</script>

<style scoped>
.fb-properties__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px;
  color: #b9b9c3;
}
.fb-properties__empty h6 {
  color: #6e6b7b;
}
.fb-properties__empty p {
  font-size: 0.82rem;
  max-width: 240px;
}
.dark-layout .fb-properties__empty h6 {
  color: #d0d2d6;
}

::v-deep .fb-properties__tabs > .nav-tabs {
  padding: 10px 12px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #ebe9f1;
}
.dark-layout ::v-deep .fb-properties__tabs > .nav-tabs {
  border-bottom-color: #3b4253;
}

.fb-option-card {
  background: #f9f9f9;
}
.dark-layout .fb-option-card {
  background: #283046;
  border-color: #3b4253 !important;
}
.fb-logic-summary {
  padding-left: 1.1rem;
  font-size: 0.85rem;
}

.fb-opt-label {
  font-size: 0.8rem;
}
</style>
